import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { logger } from "../lib/logger";
import { ArtworkCard } from "../components/ArtworkCard";
import { Instagram, Twitter, Facebook, Globe } from "lucide-react";
import Seo, { personLd, breadcrumbLd } from "../components/Seo";

const resolveAvatar = (url) => {
  if (!url) return null;
  return url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;
};

export default function ArtistProfile() {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/artists/${id}`);
        setArtist(data);
      } catch (err) {
        logger.error("Artist profile load failed:", err);
      }
    })();
  }, [id]);

  if (!artist) return <div className="p-24 label-eyebrow text-[#555]">Loading…</div>;

  const avatarSrc =
    resolveAvatar(artist.avatar_url) ||
    "https://static.prod-images.emergentagent.com/jobs/2cf555d8-2b2e-4a65-8a76-f17be07c4f4a/images/d11b06ed5c6c920763ec229eb9d9f1c7ad885e95f977d086b28da211c4d8e0ef.png";

  return (
    <div data-testid="artist-profile-page" className="px-6 md:px-12 lg:px-24 py-16">
      <Seo
        title={`${artist.name} — Contemporary African Artist`}
        description={
          artist.bio
            ? `${artist.bio.slice(0, 160)}${artist.bio.length > 160 ? "…" : ""}`
            : `Discover ${artist.name}, contemporary African artist${artist.country ? ` from ${artist.country}` : ""}. Browse original works at AfricaArtMasters.`
        }
        type="profile"
        image={avatarSrc}
        imageAlt={artist.name}
        path={`/artist/${artist.id}`}
        jsonLd={[
          personLd({ artist: { ...artist, avatar_url: avatarSrc }, url: `${window.location.origin}/artist/${artist.id}` }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Artists", path: "/artists" },
            { name: artist.name, path: `/artist/${artist.id}` },
          ]),
        ]}
      />
      <Link to="/artists" className="label-eyebrow text-[#555] hover:text-[#1E3A8A]">
        ← All Artists
      </Link>
      <div className="grid grid-cols-12 gap-y-10 gap-x-4 lg:gap-10 mt-10">
        <div className="col-span-12 lg:col-span-5">
          <div className="aspect-[4/5] bg-[#F2EDE5]">
            <img
              src={avatarSrc}
              alt={`${artist.name}, contemporary African artist${artist.country ? ` from ${artist.country}` : ""}`}
              className="w-full h-full object-cover grayscale"
            />
          </div>
        </div>
        <div className="col-span-12 lg:col-span-7">
          <p className="label-eyebrow text-[#555]">— Artist</p>
          <h1 data-testid="artist-name" className="font-serif-display text-6xl md:text-7xl tracking-tighter leading-none font-light mt-4">
            {artist.name}
          </h1>
          <p className="label-eyebrow text-[#1E3A8A] mt-6">{artist.country}</p>
          {artist.art_center && (
            <Link
              to={`/art-hub/${artist.art_center.id}`}
              data-testid="artist-center-chip"
              className="inline-block mt-4 border border-[#1E3A8A]/30 px-3 py-1.5 label-eyebrow text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors"
            >
              Member of {artist.art_center.name}
            </Link>
          )}
          <p className="mt-8 text-lg text-[#333] leading-relaxed font-light max-w-xl">{artist.bio}</p>
          {artist.statement && (
            <div className="mt-8 max-w-xl" data-testid="artist-statement">
              <p className="label-eyebrow text-[#1E3A8A]">— Artist statement</p>
              <p className="mt-3 text-base text-[#333] leading-relaxed font-light italic whitespace-pre-line">
                {artist.statement}
              </p>
            </div>
          )}

          {(artist.instagram_handle || artist.twitter_handle || artist.facebook_url || artist.website_url) && (
            <div className="mt-8 flex items-center gap-4 flex-wrap" data-testid="artist-socials">
              {artist.instagram_handle && (
                <a
                  data-testid="artist-instagram"
                  href={`https://instagram.com/${artist.instagram_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-10 h-10 border border-[#E5E0D8] flex items-center justify-center hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] transition-colors"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.5} />
                </a>
              )}
              {artist.twitter_handle && (
                <a
                  data-testid="artist-twitter"
                  href={`https://x.com/${artist.twitter_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="X (Twitter)"
                  className="w-10 h-10 border border-[#E5E0D8] flex items-center justify-center hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] transition-colors"
                >
                  <Twitter className="w-4 h-4" strokeWidth={1.5} />
                </a>
              )}
              {artist.facebook_url && (
                <a
                  data-testid="artist-facebook"
                  href={artist.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="w-10 h-10 border border-[#E5E0D8] flex items-center justify-center hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] transition-colors"
                >
                  <Facebook className="w-4 h-4" strokeWidth={1.5} />
                </a>
              )}
              {artist.website_url && (
                <a
                  data-testid="artist-website"
                  href={artist.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Website"
                  className="w-10 h-10 border border-[#E5E0D8] flex items-center justify-center hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] transition-colors"
                >
                  <Globe className="w-4 h-4" strokeWidth={1.5} />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="hairline-divider my-20" />

      <div>
        <p className="label-eyebrow text-[#555]">— Works by {artist.name}</p>
        <h2 className="font-serif-display text-4xl tracking-tight font-light mt-2 mb-10">
          The Collection
        </h2>
        {artist.artworks?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {artist.artworks.map((art, i) => (
              <ArtworkCard key={art.id} artwork={art} aspect={i % 3 === 0 ? "tall" : "square"} />
            ))}
          </div>
        ) : (
          <p className="font-serif-display text-2xl text-[#555]">No works on view yet.</p>
        )}
      </div>
    </div>
  );
}
